import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://loangorilla.com.au";

// Loan category page configuration for FinancialProduct schema
const LOAN_CATEGORIES: Record<
  string,
  { name: string; description: string; loanType: string; category: string }
> = {
  "/car-loans": {
    name: "Car Loans Australia",
    description:
      "Compare car loan rates from 40+ Australian lenders. New, used, refinance, novated lease and bad credit options.",
    loanType: "Car Loan",
    category: "Auto Loan",
  },
  "/home-loans": {
    name: "Home Loans Australia",
    description:
      "Compare home loan rates from 130+ Australian lenders. First home, refinance, investment, fixed and variable rate options.",
    loanType: "Home Loan",
    category: "Mortgage",
  },
  "/personal-loans": {
    name: "Personal Loans Australia",
    description:
      "Compare personal loan rates from 30+ Australian lenders. Secured, unsecured, debt consolidation and low-rate options.",
    loanType: "Personal Loan",
    category: "Personal Loan",
  },
  "/business-loans": {
    name: "Business Loans Australia",
    description:
      "Compare business loan rates from 40+ Australian lenders. Term loans, unsecured, equipment finance and working capital.",
    loanType: "Business Loan",
    category: "Business Loan",
  },
};

const SECTION_LABELS: Record<string, string> = {
  "car-loans": "Car Loans",
  "home-loans": "Home Loans",
  "personal-loans": "Personal Loans",
  "business-loans": "Business Loans",
  calculators: "Calculators",
  guides: "Guides",
  about: "About",
  contact: "Contact",
  faq: "FAQ",
  "how-it-works": "How It Works",
  "privacy-policy": "Privacy Policy",
  "terms-of-use": "Terms of Use",
  "comparison-engine": "Comparison Engine",
  choose: "Choose",
};

function titleCase(slug: string): string {
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function labelFor(segment: string): string {
  return SECTION_LABELS[segment] || titleCase(segment);
}

function buildBreadcrumbs(pathname: string) {
  const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (!segments.length) return [];
  const items = [{ name: "Home", url: `${SITE}/` }];
  let acc = "";
  for (const seg of segments) {
    acc += `/${seg}`;
    items.push({ name: labelFor(seg), url: `${SITE}${acc}` });
  }
  return items;
}

function getCategoryKey(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  // Only the top-level loan category landing pages
  if (LOAN_CATEGORIES[clean]) return clean;
  return null;
}

const SCHEMA_ATTR = "data-route-schema";

function injectSchema(id: string, data: Record<string, any>) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(SCHEMA_ATTR, id);
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

function clearSchemas() {
  document.head
    .querySelectorAll(`script[${SCHEMA_ATTR}]`)
    .forEach((el) => el.remove());
}

export default function RouteSchema() {
  const { pathname } = useLocation();

  useEffect(() => {
    const clean = pathname.replace(/\/+$/, "") || "/";
    const url = `${SITE}${clean === "/" ? "/" : clean}`;

    clearSchemas();

    // Organization — every page
    injectSchema("organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "LoanGorilla",
      url: SITE,
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/favicon.png`,
        width: 516,
        height: 508,
      },
      description:
        "Australian loan comparison and broker service for car, home, personal and business loans.",
      areaServed: { "@type": "Country", name: "Australia" },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        availableLanguage: "English",
        areaServed: "AU",
      },
    });

    // WebSite — every page
    injectSchema("website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      name: "LoanGorilla",
      url: SITE,
      inLanguage: "en-AU",
      publisher: { "@id": `${SITE}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE}/car-loans?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });

    // BreadcrumbList — non-home routes
    if (clean !== "/") {
      const crumbs = buildBreadcrumbs(clean);
      if (crumbs.length > 1) {
        injectSchema("breadcrumb", {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: c.url,
          })),
        });
      }
    }

    // FinancialProduct — loan category landing pages
    const catKey = getCategoryKey(clean);
    if (catKey) {
      const cat = LOAN_CATEGORIES[catKey];
      injectSchema("financialproduct", {
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        name: cat.name,
        description: cat.description,
        url,
        category: cat.category,
        loanType: cat.loanType,
        areaServed: { "@type": "Country", name: "AU" },
        provider: { "@id": `${SITE}/#organization` },
        feesAndCommissionsSpecification: `${SITE}/terms-of-use`,
      });
    }

    return () => {
      clearSchemas();
    };
  }, [pathname]);

  return null;
}